import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Linking, Text } from 'react-native';
import {
  getCloudinaryUrl,
  getFormattedExpirationDate,
  LoyaltyOfferCard as OfferCard,
  useResponsive,
} from '@sg/garnish';
import { type Challenge, ChallengeProgressType } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const LoyaltyOfferCard = (props: LoyaltyOfferCardProps) => {
  const {
    offer,
    isLoading,
    layout = 'standard',
    rounded,
    onStartButtonPress,
    onOrderButtonPress,
  } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const expirationDate = getFormattedExpirationDate(
    offer.expirationDate,
    formatMessage(messages.endsPrefix),
  );
  const formattedImageUrl = offer.assetUrl
    ? match([
        getFormattedAssetUrl(offer.assetUrl).XS,
        getFormattedAssetUrl(offer.assetUrl).SM,
      ])
    : undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const openTerms = useCallback(() => {
    void Linking.openURL(TERMS_LINK);
  }, []);

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldDisplayTag = checkIfShouldDisplayTag(offer) && layout === 'standard'; // prettier-ignore
  const shouldDisplayProgressTag = checkIfShouldDisplayProgressTag(offer) && layout === 'standard'; // prettier-ignore
  const shouldDisplayFloatingLabel = checkIfShouldDisplayFloatingLabel(offer);
  const shouldDisplayLabel = checkIfShouldDisplayLabel(offer) && layout === 'standard'; // prettier-ignore
  const shouldDisplayStartButton = checkIfShouldDisplayStartButton(offer);
  const shouldDisplayOrderButton = checkIfShouldDisplayOrderButton(offer);
  const shouldDisplayStepProgressBar = checkIfShouldDisplayStepProgressBar(offer); // prettier-ignore
  const shouldDisplaySpendProgressBar = checkIfShouldDisplaySpendProgressBar(offer); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  return (
    <OfferCard.Container rounded={rounded}>
      <OfferCard.Image
        source={formattedImageUrl}
        size={layout === 'compact' ? 'small' : 'large'}
      />

      {shouldDisplayFloatingLabel ? (
        <OfferCard.FloatingLabel>
          <FormattedMessage {...messages.labelCompleted} />
        </OfferCard.FloatingLabel>
      ) : null}

      <OfferCard.ContentContainer
        size={layout === 'compact' ? 'small' : 'large'}
      >
        {shouldDisplayTag ? (
          <OfferCard.Tag>
            <FormattedMessage {...messages.tagNew} />
          </OfferCard.Tag>
        ) : null}

        {shouldDisplayProgressTag ? (
          <OfferCard.Tag palette="forest">{offer.progressCopy}</OfferCard.Tag>
        ) : null}

        {shouldDisplayLabel ? <LoyaltyOfferCardLabel offer={offer} /> : null}

        {/* ──────────────────────────────────────────────────────────────── */}

        <OfferCard.Title sizeMatch={layout === 'standard' ? ['36'] : ['24']}>
          {offer.title}
        </OfferCard.Title>

        {/* ─── Description and Terms ────────────────────────────────────── */}

        <OfferCard.Text>
          {offer.descriptiveTitle}

          {expirationDate ? (
            <>
              <Text> </Text>
              <OfferCard.Text>{expirationDate}</OfferCard.Text>
              <Text>.</Text>
            </>
          ) : null}

          <Text> </Text>

          {layout === 'standard' ? (
            <OfferCard.PressableText onPress={openTerms}>
              <FormattedMessage {...messages.details} />
              <Text>.</Text>
            </OfferCard.PressableText>
          ) : null}
        </OfferCard.Text>

        {/* ─── Progress Bars ────────────────────────────────────────────── */}

        {shouldDisplaySpendProgressBar || shouldDisplayStepProgressBar ? (
          <OfferCard.ProgressBarContainer
            size={layout === 'compact' ? 'small' : 'large'}
          >
            {shouldDisplaySpendProgressBar ? (
              <OfferCard.ProgressBar
                percentageCompleted={offer.percentageCompleted}
                accessibilityLabel={formatMessage(
                  messages.progressBarA11yLabel,
                  { progress: offer.percentageCompleted },
                )}
              />
            ) : null}

            {shouldDisplayStepProgressBar ? (
              <OfferCard.ProgressStepper
                currentStep={offer.progress}
                steps={offer.goal}
                percentageCompleted={offer.percentageCompleted}
                accessibilityLabel={formatMessage(
                  messages.progressBarA11yLabel,
                  { progress: offer.percentageCompleted },
                )}
              />
            ) : null}
          </OfferCard.ProgressBarContainer>
        ) : null}
      </OfferCard.ContentContainer>

      {/* ─── Footer ─────────────────────────────────────────────────────── */}

      {layout === 'compact' ? (
        <OfferCard.Footer size="small">
          <OfferCard.PressableText onPress={openTerms}>
            <FormattedMessage {...messages.details} />
          </OfferCard.PressableText>

          {shouldDisplayStartButton ? (
            <OfferCard.Button
              size="medium"
              onPress={onStartButtonPress}
              isLoading={isLoading}
            >
              <FormattedMessage {...messages.startButtonLabel} />
            </OfferCard.Button>
          ) : null}

          {shouldDisplayOrderButton ? (
            <OfferCard.Button
              size="xsmall"
              onPress={onOrderButtonPress}
              palette="white"
            >
              {offer.progressCopy}
            </OfferCard.Button>
          ) : null}
        </OfferCard.Footer>
      ) : null}

      {layout === 'standard' &&
      (shouldDisplayStartButton || shouldDisplayOrderButton) ? (
        <OfferCard.Footer size="medium">
          {shouldDisplayStartButton ? (
            <OfferCard.Button
              fluid
              size={match(['large', 'large-wide'])}
              onPress={onStartButtonPress}
              isLoading={isLoading}
            >
              <FormattedMessage {...messages.startButtonLabel} />
            </OfferCard.Button>
          ) : null}

          {shouldDisplayOrderButton ? (
            <OfferCard.Button
              fluid
              size={match(['large', 'large-wide'])}
              onPress={onOrderButtonPress}
              palette="white"
            >
              <FormattedMessage {...messages.orderNowButtonLabel} />
            </OfferCard.Button>
          ) : null}
        </OfferCard.Footer>
      ) : null}
    </OfferCard.Container>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const LoyaltyOfferCardLabel = (props: LoyaltyOfferCardLabelProps) => {
  const { offer } = props;

  if (offer.status === 'STARTED') {
    return (
      <OfferCard.Label>
        <FormattedMessage {...messages.labelStarted} />
      </OfferCard.Label>
    );
  }

  if (offer.status === 'IN_PROGRESS') {
    return (
      <OfferCard.Label>
        <FormattedMessage {...messages.labelInProgress} />
      </OfferCard.Label>
    );
  }

  return null;
};

// ─── Utils ───────────────────────────────────────────────────────────────────

function checkIfShouldDisplayTag(offer: Challenge) {
  return offer.status === 'NOT_STARTED';
}

function checkIfShouldDisplayProgressTag(offer: Challenge) {
  return offer.status === 'IN_PROGRESS';
}

function checkIfShouldDisplayLabel(offer: Challenge) {
  return offer.status === 'STARTED';
}

function checkIfShouldDisplayProgressBar(offer: Challenge) {
  return offer.status === 'STARTED' || offer.status === 'IN_PROGRESS';
}

function checkIfShouldDisplayStepProgressBar(offer: Challenge) {
  return (
    checkIfShouldDisplayProgressBar(offer) &&
    offer.progressType === ChallengeProgressType.Step
  );
}

function checkIfShouldDisplaySpendProgressBar(offer: Challenge) {
  return (
    checkIfShouldDisplayProgressBar(offer) &&
    offer.progressType === ChallengeProgressType.Spend
  );
}

function checkIfShouldDisplayStartButton(offer: Challenge) {
  return offer.status === 'NOT_STARTED';
}

function checkIfShouldDisplayOrderButton(offer: Challenge) {
  return offer.status === 'STARTED' || offer.status === 'IN_PROGRESS';
}

function checkIfShouldDisplayFloatingLabel(offer: Challenge) {
  return offer.status === 'COMPLETED';
}

function getFormattedAssetUrl(assetUrl: string) {
  return {
    XS: getCloudinaryUrl(assetUrl, { height: IMAGE_SIZE.XS }),
    SM: getCloudinaryUrl(assetUrl, { height: IMAGE_SIZE.SM }),
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  tagNew: {
    defaultMessage: 'New!',
    description: 'Loyalty | Offer Card | Tag | New',
  },
  details: {
    defaultMessage: 'Details',
    description: 'Loyalty | Offer Card | Details',
  },
  labelStarted: {
    defaultMessage: 'Started',
    description: 'Loyalty | Offer Card | Label | Started',
  },
  labelInProgress: {
    defaultMessage: 'In progress!',
    description: 'Loyalty | Offer Card | Label | In progress',
  },
  labelCompleted: {
    defaultMessage: 'Completed',
    description: 'Loyalty | Offer Card | Label | Completed',
  },
  endsPrefix: {
    defaultMessage: 'Ends',
    description: 'Loyalty | Offer Card | Ends prefix',
  },
  startButtonLabel: {
    defaultMessage: 'Start',
    description: 'Loyalty | Offer Card | Button | Start',
  },
  orderNowButtonLabel: {
    defaultMessage: 'Order now',
    description: 'Loyalty | Offer Card | Button | Order now',
  },
  progressBarA11yLabel: {
    defaultMessage: `Offer completion progress is {progress}%`,
    description: 'Loyalty | Offer Card | Progress bar | a11y label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const TERMS_LINK = 'https://www.sweetgreen.com/terms';
const IMAGE_SIZE = {
  XS: 190,
  SM: 330,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LoyaltyOfferCardProps = {
  offer: Challenge;
  isLoading: boolean;
  rounded?: boolean;
  layout?: 'compact' | 'standard';
  onStartButtonPress: () => void;
  onOrderButtonPress: () => void;
};

type LoyaltyOfferCardLabelProps = {
  offer: Challenge;
};
